export interface ISeatArrangement {
  /** The viewer's own seat, pinned to the bottom of the left sidebar. */
  me?: IUser
  /** Left sidebar, top to bottom, excluding the viewer's own card. */
  left: IUser[]
  /** Right sidebar, top to bottom. */
  right: IUser[]
}

/**
 * Decide where each player's card goes for *this* viewer.
 *
 * Your own card always sits in the same place — bottom of the left sidebar,
 * right above the trade button and the bank — so after a game or two you stop
 * hunting for your own money and just look there. Everyone else fills the rest
 * of the two columns from the bottom up, alternating right and left, which keeps
 * the seat that plays straight after you nearest to your own card and leaves the
 * columns evenly filled at any player count.
 *
 * Only the *visual* seat moves. `player.order` is untouched, so turn order,
 * piece colours, animations and test ids all behave exactly as before.
 */
export const getSeatArrangement = (
  players: IUser[] | undefined,
  myEmail: string
): ISeatArrangement => {
  const all = players || []
  const myIndex = all.findIndex((player) => player.email === myEmail)
  // Spectators (and anything odd, like a game we aren't seated in) keep the old
  // even/odd split — there is no "own card" to pin.
  if (myIndex < 0) {
    return {
      me: undefined,
      left: all.filter((_, index) => index % 2 === 0),
      right: all.filter((_, index) => index % 2 === 1),
    }
  }

  // Everyone else in turn order starting from whoever plays after us, so the
  // cycle still reads around the table from our own card.
  const others = [...all.slice(myIndex + 1), ...all.slice(0, myIndex)]
  const rightBottomUp = others.filter((_, index) => index % 2 === 0)
  const leftBottomUp = others.filter((_, index) => index % 2 === 1)

  return {
    me: all[myIndex],
    // Filled bottom-up above, rendered top-down here.
    left: leftBottomUp.reverse(),
    right: rightBottomUp.reverse(),
  }
}
